import * as dateFnsCompat from 'utils/date-fns-compat';

import he from 'he';
import fp from "lodash/fp";
import React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { push } from 'react-router-redux';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { dateFormats, routes } from '../../../../../constants';
import { forbidPropagate, getCpdCategory, getCpdHours, isBusinessAdmin, isBusinessUser, isExternalUser } from '../../../../../services';
import { taskActions } from '../../../../../state/modules/task';
import { userTaskHistorySelector } from '../../../../../state/modules/task/selectors';
import { currentUserSelector } from '../../../../../state/modules/user';
import { Icon, SortableTable } from '../../../../components';
import Loading from '../../../loading';

const getTaskCompletionDate = (task) => fp.flow(
  fp.get('users.[0].completionDate'),
  (date) => <span>
    {
      fp.isEmpty(date) ? '' : dateFnsCompat.format(dateFnsCompat.parse(date), dateFormats.ShortDate)
    }
  </span>
)(task)

const formatRow = (allCpdCategorys, industryCpdCategory, goTo) => (task) => ({
  'handler': () => goTo(routes.app.tasks.view(task.id)),
  'title': <LinkContainer onClick={forbidPropagate} to={routes.app.tasks.view(task.id)}><span>{he.decode(task.name)}</span></LinkContainer>,
  'dueDate': <span>{dateFnsCompat.format(dateFnsCompat.parse(task.dueDate), dateFormats.ShortDate)}</span>,
  'status': fp.isEmpty(task.users[0].completionDate) ? <span>Archived</span> : <span>Completed</span>,
  'completionDate': getTaskCompletionDate(task),
  'cpdCategory': getCpdCategory(task, allCpdCategorys, industryCpdCategory),
  'cpdHours': getCpdHours(task),
  'assignedBy': <span>{he.decode(task.assigner)}</span>,
  'operations': <LinkContainer onClick={forbidPropagate} className='pull-right' to={routes.app.tasks.view(task.id)}><Button bsStyle='plain'><Icon icon='chevron-right-large' /></Button></LinkContainer>
})

const columns = [
  {
    name: 'title',
    display: () => 'Title',
    className: () => "title",
    sortable: true
  },
  {
    name: 'cpdCategory',
    display: () => 'CPD Category',
    className: () => "cpdCategory"
  },
  {
    name: 'cpdHours',
    display: () => 'CPD hours',
    className: () => "cpdHours"
  },
  {
    name: 'assignedBy',
    display: () => 'Assigned By',
    className: () => "assigner"
  },
  {
    name: 'dueDate',
    display: () => 'Due Date',
    className: () => "dueDate",
    sortable: true
  },
  {
    name: 'status',
    display: () => 'Status',
    className: () => "status",
  },
  {
    name: 'completionDate',
    display: () => 'Completion Date',
    className: () => "completionDate",
    sortable: true
  },
  {
    name: 'operations',
    display: () => '',
    className: () => "operations"
  }
]

const CompletedTasksTable = ({
  rows,
  isFetching,
  onDateRangeChange,
  loadMore,
  sortBy,
  filter
}) => (
    <div id='completed-tasks' className='scroll-table'>
      {
        rows.length > 0 ?
          <SortableTable
            className='table--tasks completed-tasks-table'
            isFetching={isFetching}
            unclickable={false}
            rows={rows}
            sortBy={sortBy}
            columns={columns} /> :
          <Row className='no-gutter top-gap'>
            <Col xs={12} md={6} mdOffset={3} className='boxed'>
              <div className='complete task--empty-data'>
                <Icon icon='caddie-the-dog' />
                <h1>No completed tasks</h1>
                <p className='no-margin'>Your task history will appear once you have completed your first task</p>
              </div>
            </Col>
          </Row>

      }
      {
        ((rows.length > 0) && (rows.length % 20 === 0)) ?
          <Row className='no-gutter row-slider'>
            <Col xs={12} md={4} mdOffset={4}>
              <Button
                className='large full'
                onClick={loadMore}>
                Load more task history
              </Button>
            </Col>
          </Row> :
          null
      }
    </div>
  );

const mapStateToProps = (state) => ({
  user: currentUserSelector(state),
  history: userTaskHistorySelector(state),
  isFetching: state.task.isFetchingHistory,
  allCpdCategorys: state.cpdCategorys.cpdCategorys,
  industryCpdCategory: state.industry.cpdCategorys
})

const mapDispatchToProps = (dispatch) => ({
  getHistory: bindActionCreators(taskActions.task.getHistory, dispatch),
  loadMoreTasks: bindActionCreators(taskActions.task.loadMore, dispatch),
  goTo: bindActionCreators(push, dispatch)
})


export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  branch(
    ({ user }) => !isBusinessAdmin(user) && !isBusinessUser(user) && !isExternalUser(user),
    renderComponent(Loading)
  ),
  withStateHandlers(
    () => ({
      filter: {
        skip: 0,
        sort: '',
        take: 20
      },
      noMoreToLoad: false
    }),
    {
      onQuerying: () => (filter) => ({
        filter,
        noMoreToLoad: false
      })
    }
  ),
  lifecycle({
    componentDidMount() {
      const {
        getHistory,
        filter
      } = this.props;
      getHistory(filter);
    }
  }),
  withHandlers({
    sortBy: ({ getHistory, filter, onSort, onQuerying }) => (field, sortOrder) => {
      const sortParam = sortOrder ? `${field},${sortOrder}` : undefined;
      const nextFilter = { ...filter, sort: sortParam, skip: 0 };
      onQuerying(nextFilter);
      getHistory(nextFilter);
    },
    loadMore: ({ loadMoreTasks, filter, completedTasks, onQuerying }) => () => {
      const nextFilter = { ...filter, skip: completedTasks.length };
      onQuerying(nextFilter);
      loadMoreTasks(nextFilter);
    },
    onDateRangeChange: ({ getHistory, filter, onQuerying, setDateRange }) => (key, date) => {
      const nextFilter = { ...filter, [key]: key === 'toDate' ? dateFnsCompat.format(dateFnsCompat.endOfDay(date)) : dateFnsCompat.format(date), skip: 0 };
      onQuerying(nextFilter);
      if (nextFilter.fromDate && nextFilter.toDate) {
        getHistory(nextFilter);
      }
    }
  }),
  withProps(
    ({ history, allCpdCategorys, industryCpdCategory, goTo }) => ({
      rows: fp.map(
        formatRow(allCpdCategorys, industryCpdCategory, goTo)
      )(history)
    })
  )
)(CompletedTasksTable);



// WEBPACK FOOTER //
// ./src/containers/pages/app/tasks/partials/completed-tasks-table.js